// screens/marketplace/coursesApi.js
//
// Thin wrappers around the marketplace courses endpoints.
//
// Backend contract (from the courses spec):
//   GET    /marketplace/courses/                        → public list
//   GET    /marketplace/courses/{id}/                   → public detail
//   POST   /marketplace/courses/                        → admin create (Batch 3)
//   PATCH  /marketplace/courses/{id}/                   → admin update (Batch 3)
//   DELETE /marketplace/courses/{id}/                   → admin deactivate (Batch 3)
//   POST   /marketplace/courses/{id}/enroll/            → buyer enroll
//   GET    /marketplace/courses/enrollments/            → my enrollments
//   GET    /marketplace/courses/enrollments/{id}/       → single enrollment
//   GET    /marketplace/courses/admin/enrollments/      → admin list (Batch 3)
//   POST   /marketplace/courses/pricing/                → price override (Batch 3)
//
// Every caller passes the raw `authFetch` so this module stays
// independent of the AuthProvider shape.

const readApiError = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  const errs = data.errors;
  if (errs) {
    const parts = [];
    for (const [key, val] of Object.entries(errs)) {
      const arr = Array.isArray(val) ? val : [val];
      arr.forEach((msg) => {
        if (key === "non_field_errors" || key === "detail") parts.push(msg);
        else parts.push(`${key.replace(/_/g, " ")}: ${msg}`);
      });
    }
    if (parts.length) return parts.join(" ");
  }
  return fallback;
};

/* ══════════════════════════════════════════════════════════
   Reading
   ══════════════════════════════════════════════════════════ */

export const listCourses = async (authFetch, { search } = {}) => {
  const qs = new URLSearchParams();
  if (search) qs.set("search", search);
  const suffix = qs.toString() ? `?${qs}` : "";
  const res = await authFetch(`/marketplace/courses/${suffix}`);
  const data = await res.json();
  if (!res.ok) throw new Error(readApiError(data, "Could not load courses"));
  return data.data || [];
};

export const getCourse = async (authFetch, id) => {
  const res = await authFetch(`/marketplace/courses/${id}/`);
  const data = await res.json();
  if (!res.ok) throw new Error(readApiError(data, "Could not load course"));
  return data.data;
};

export const listMyEnrollments = async (authFetch) => {
  const res = await authFetch("/marketplace/courses/enrollments/");
  const data = await res.json();
  if (!res.ok)
    throw new Error(readApiError(data, "Could not load your enrollments"));
  return data.data || [];
};

export const getEnrollment = async (authFetch, id) => {
  const res = await authFetch(`/marketplace/courses/enrollments/${id}/`);
  const data = await res.json();
  if (!res.ok)
    throw new Error(readApiError(data, "Could not load enrollment"));
  return data.data;
};

/* ══════════════════════════════════════════════════════════
   Writing
   ══════════════════════════════════════════════════════════ */

/**
 * Enroll in a course.
 *
 * Payload must include contact + telegram + payment method. For
 * Paystack, the response carries `authorization_url` (redirect the
 * user). For Wallet, the response carries the full enrollment object
 * including `telegram_link` on success.
 */
export const enrollInCourse = async (authFetch, courseId, payload) => {
  const res = await authFetch(`/marketplace/courses/${courseId}/enroll/`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(readApiError(data, "Could not enroll"));
  return data.data;
};

/* ══════════════════════════════════════════════════════════
   Helpers
   ══════════════════════════════════════════════════════════ */

/**
 * Enrollment status → display label + tone, reused by the list and
 * detail pages.
 */
export const enrollmentStatusMeta = (status) => {
  switch (status) {
    case "PAID":
      return { label: "Paid", tone: "success" };
    case "PENDING":
      return { label: "Pending payment", tone: "warning" };
    case "FAILED":
      return { label: "Failed", tone: "error" };
    default:
      return { label: status || "Unknown", tone: "muted" };
  }
};

/**
 * True when a Paystack enrollment has completed payment and the
 * Telegram link should be revealed.
 */
export const isPaid = (enrollment) =>
  enrollment?.status === "PAID" || enrollment?.payment_status === "COMPLETED";